import { Select } from "antd";
import { STORE_REQUISITION_LIST } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";

const { Option } = Select;

const getRequisitionNumber = (item) =>
  item?.requisition_no || item?.requisition_number || null;

const StoreRequisitionNoSelect = ({
  movementType,
  value,
  onChange,
  placeholder = "Search requisition no.",
  size = "small",
  disabled = false,
  allowClear = true,
  valueKey = "id",
  onRequisitionChange,
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRequisitions = useCallback(
    async (search = "") => {
      if (!movementType) {
        setOptions([]);
        return;
      }

      setLoading(true);
      const response = await getData(`${STORE_REQUISITION_LIST}?page=1`, false, {
        per_page: 50,
        movement_type: movementType,
        ...(search ? { requisition_no: search } : {}),
      });

      if (response?.data?.code === 200) {
        setOptions(response?.data?.data?.data || []);
      } else {
        setOptions([]);
      }

      setLoading(false);
    },
    [movementType],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue) => {
        fetchRequisitions(searchValue);
      }, 400),
    [fetchRequisitions],
  );

  useEffect(() => {
    fetchRequisitions();
    return () => debouncedSearch.cancel();
  }, [fetchRequisitions, debouncedSearch]);

  const displayOptions = useMemo(() => {
    if (!value) return options;

    const exists = options.some((item) => {
      const optionValue =
        valueKey === "requisition_no"
          ? getRequisitionNumber(item)
          : item?.id;
      return String(optionValue) === String(value);
    });

    if (exists) return options;

    // Keep selected value visible even if not in current page/search results
    if (valueKey === "requisition_no") {
      return [
        {
          id: `selected-${value}`,
          requisition_no: String(value),
        },
        ...options,
      ];
    }

    return [
      {
        id: value,
        requisition_no: null,
      },
      ...options,
    ];
  }, [options, value, valueKey]);

  const handleChange = (selectedValue, option) => {
    const selectedItem = displayOptions.find((item) => {
      const optionValue =
        valueKey === "requisition_no"
          ? getRequisitionNumber(item)
          : item?.id;
      return String(optionValue) === String(selectedValue);
    });

    const requisitionNumber =
      getRequisitionNumber(selectedItem) ||
      option?.requisitionnumber ||
      option?.["data-requisition-no"] ||
      (typeof option?.children === "string" ? option.children : null);

    onChange?.(selectedValue ?? null);
    onRequisitionChange?.(selectedValue ?? null, requisitionNumber || null);
  };

  return (
    <Select
      showSearch
      allowClear={allowClear}
      placeholder={placeholder}
      optionFilterProp="children"
      filterOption={false}
      size={size}
      disabled={disabled || !movementType}
      value={value}
      onChange={handleChange}
      loading={loading}
      onSearch={debouncedSearch}
      onClear={() => {
        onChange?.(null);
        onRequisitionChange?.(null, null);
        fetchRequisitions();
      }}
    >
      {displayOptions.map((item) => {
        const requisitionNumber = getRequisitionNumber(item);
        const optionValue =
          valueKey === "requisition_no" ? requisitionNumber : item.id;

        return (
          <Option
            key={item.id}
            value={optionValue}
            requisitionnumber={requisitionNumber}
            data-requisition-no={requisitionNumber}
          >
            {requisitionNumber}
          </Option>
        );
      })}
    </Select>
  );
};

export default StoreRequisitionNoSelect;
